import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Props {
  onClose: () => void;
  listName: string;
  endpoint: string;
}

export function SettingsImportModal({ onClose, listName, endpoint }: Props) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[]>([]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onFile = async (f: File) => {
    setFile(f);
    const text = await f.text();
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    setPreview(lines.slice(0, 20));
  };

  const save = useMutation({
    mutationFn: async () => {
      const items = preview.filter(Boolean).map((v) => ({ value: v }));
      return Promise.all(items.map((i) => api.post(endpoint, i).catch(() => null)));
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success('Imported', `${preview.length} items`);
      onClose();
    },
    onError: () => toast.error('Import failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="si-list-title">Import {listName}</div>
        <div className="modal-sub">Upload a CSV, Excel, or plain text file</div>
        <div className="form-row">
          <label className="form-label">Format</label>
          <div id="si-list-hint" style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.7, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 7, padding: 9, marginBottom: 10 }}>
            One value per line. CSV: first column used, header row optional.
          </div>
        </div>
        <div
          className="drop-zone"
          id="si-list-dropzone"
          style={{ padding: 14, borderRadius: 8, border: '2px dashed var(--border)', textAlign: 'center', cursor: 'pointer' }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
          onClick={() => document.getElementById('si-list-file')?.click()}
        >
          <div style={{ fontSize: 20, marginBottom: 5 }}>📂</div>
          <div style={{ fontSize: 12, fontWeight: 500 }}>{file ? `📎 ${file.name}` : 'Drop CSV, Excel, or text file here'}</div>
          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>or click to browse</div>
          <input type="file" id="si-list-file" accept=".csv,.xlsx,.xls,.txt" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        </div>
        {preview.length > 0 ? (
          <div id="si-list-preview" style={{ marginTop: 11, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, maxHeight: 160, overflowY: 'auto', fontSize: 11 }}>
            {preview.map((p, i) => <div key={i} style={{ padding: '2px 0', borderBottom: '1px solid rgba(30,42,64,.3)' }}>{p}</div>)}
          </div>
        ) : null}
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {preview.length > 0 ? (
            <button className="btn btn-primary" id="si-list-confirm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Importing…' : `✓ Import ${preview.length} Items`}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
